import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { FiCalendar, FiShield, FiFileText } from 'react-icons/fi';

import { Page, LayoutComposer } from '../../../components';
import TopbarContainer from '../../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../../containers/FooterContainer/FooterContainer';

import css from './LegalDocLayout.module.css';

/**
 * Reusable layout for Legal, Terms, and Policy documents on ChinaDolphins.
 */
const LegalDocLayout = ({
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  jurisdiction,
  tocItems,
  children,
  schemaType,
}) => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title={`${title} | ChinaDolphins`}
      description={subtitle || `${title} for ChinaDolphins B2B Sourcing Platform`}
      schemaType={schemaType || 'Article'}
      className={css.pageWrapper}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Header Hero ── */}
                <div className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.breadcrumbs}>
                      <Link to="/" className={css.breadcrumbLink}>Home</Link>
                      <span>/</span>
                      <span>Legal & Compliance</span>
                    </div>

                    <h1 className={css.heroTitle}>{title}</h1>
                    {subtitle && <p className={css.heroSubtitle}>{subtitle}</p>}

                    <div className={css.metaRow}>
                      {effectiveDate && (
                        <div className={css.metaBadge}>
                          <FiCalendar size={13} />
                          <span>Effective: {effectiveDate}</span>
                        </div>
                      )}
                      {lastUpdated && (
                        <div className={css.metaBadge}>
                          <FiCalendar size={13} />
                          <span>Updated: {lastUpdated}</span>
                        </div>
                      )}
                      {jurisdiction && (
                        <div className={classNames(css.metaBadge, css.metaBadgeHighlight)}>
                          <FiShield size={13} />
                          <span>Governing: {jurisdiction}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Main Body with Sidebar TOC ── */}
                <div className={css.bodyWrapper}>
                  <div className={css.layoutGrid}>
                    {/* Table of Contents */}
                    {tocItems && tocItems.length > 0 && (
                      <aside className={css.sidebar} aria-label="Table of Contents">
                        <h3 className={css.sidebarTitle}>
                          <FiFileText style={{ marginRight: 6 }} /> Contents
                        </h3>
                        <ul className={css.tocList}>
                          {tocItems.map((item, idx) => (
                            <li key={idx}>
                              <a href={`#${item.id}`} className={css.tocLink}>
                                {item.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </aside>
                    )}

                    {/* Main Legal Content */}
                    <article className={css.contentCard}>
                      {children}
                    </article>
                  </div>
                </div>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

LegalDocLayout.defaultProps = {
  subtitle: null,
  effectiveDate: null,
  lastUpdated: null,
  jurisdiction: 'Hong Kong (Mtn Global Limited)',
  tocItems: [],
  schemaType: 'Article',
};

LegalDocLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  effectiveDate: PropTypes.string,
  lastUpdated: PropTypes.string,
  jurisdiction: PropTypes.string,
  tocItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  children: PropTypes.node.isRequired,
  schemaType: PropTypes.string,
};

export default LegalDocLayout;
